import { useEffect, useState } from "react";
import { Star, Quote, Loader2 } from "lucide-react";
import { listTestimonianze } from "@/lib/api";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
} from "@/components/ui/carousel";

const MESI = [
  "Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno",
  "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre",
];

function formatDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return `${MESI[d.getMonth()]} ${d.getFullYear()}`;
}

function TestimonianzaCard({ t, index }) {
  return (
    <div
      className="bg-white rounded-3xl p-8 shadow-sm border border-[#E2E8F0] hover:shadow-lg transition-shadow duration-300 relative h-full"
      data-testid={`testimonial-${index}`}
    >
      <div className="absolute top-6 right-6 text-[#E2E8F0]">
        <Quote className="w-10 h-10" />
      </div>

      {t.rating ? (
        <div className="flex gap-1 mb-4">
          {[...Array(t.rating)].map((_, i) => (
            <Star key={i} className="w-5 h-5 fill-[#FF9500] text-[#FF9500]" />
          ))}
        </div>
      ) : null}

      <p className="text-[#64748B] leading-relaxed mb-6 text-base line-clamp-6">
        "{t.testo}"
      </p>

      <div className="flex items-center justify-between mt-auto">
        <div>
          <p className="font-semibold text-[#0B1E36]">{t.nome}</p>
          {t.citta && (
            <p className="text-sm text-[#64748B]">{t.citta}</p>
          )}
        </div>
        {t.data_recensione && (
          <p className="text-sm text-[#64748B]">
            {formatDate(t.data_recensione)}
          </p>
        )}
      </div>
    </div>
  );
}

export default function TestimonialsSection() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    listTestimonianze()
      .then(setItems)
      .catch((err) => console.error("Errore caricamento testimonianze:", err))
      .finally(() => setLoading(false));
  }, []);

  if (!loading && items.length === 0) return null;

  const showArrows = items.length > 3;

  return (
    <section id="recensioni" className="py-24 md:py-32 bg-[#FAFAFA]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16 md:mb-20">
          <p className="text-sm font-bold uppercase tracking-[0.2em] bg-clip-text text-transparent bg-gradient-to-r from-orange-500 via-blue-500 to-purple-500 mb-4">
            Cosa Dicono i Nostri Clienti
          </p>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-[#0B1E36] mb-6">
            Recensioni dei Clienti
          </h2>
          <p className="text-base md:text-lg text-[#64748B] leading-relaxed">
            La soddisfazione dei nostri clienti è la nostra priorità.
            Ecco cosa pensano di noi.
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-[#0B1E36]" />
          </div>
        ) : (
          <>
            <div className={showArrows ? "px-0 md:px-12" : ""}>
              <Carousel
                opts={{
                  align: "start",
                  loop: items.length > 3,
                }}
                className="w-full"
              >
                <CarouselContent className="-ml-4 md:-ml-6">
                  {items.map((t, index) => (
                    <CarouselItem
                      key={t.id ?? index}
                      className="pl-4 md:pl-6 md:basis-1/2 lg:basis-1/3"
                    >
                      <TestimonianzaCard t={t} index={index} />
                    </CarouselItem>
                  ))}
                </CarouselContent>
                {showArrows && (
                  <>
                    <CarouselPrevious className="hidden md:flex -left-4 lg:-left-6" />
                    <CarouselNext className="hidden md:flex -right-4 lg:-right-6" />
                  </>
                )}
              </Carousel>
            </div>

            {/* Trust Badge */}
            <div className="mt-16 text-center">
              <div className="inline-flex items-center gap-3 bg-white px-6 py-4 rounded-full shadow-sm border border-[#E2E8F0]">
                <div className="flex -space-x-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-[#FF9500] text-[#FF9500]" />
                  ))}
                </div>
                <span className="text-[#0B1E36] font-semibold">4.9/5</span>
                <span className="text-[#64748B]">basato su {items.length}+ recensioni</span>
              </div>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
